import type { ReactNode } from "react";
import {
  alpha,
  Box,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Radio,
  Typography,
} from "@mui/material";
import moment from "moment";

import Usuario from "../../../../../models/usuario";
import { useUsuario } from "../../../../../providers/usuarioProvider";
import FelixCard from "../../../../util/ui/felixCard";
import { updateDoc } from "firebase/firestore";
import UsuarioDb from "../../../../../db/usuarioDb";
import { IconArchive, IconCalculator } from "@tabler/icons-react";
import { Perfil } from "../../../../../models/enum";
import FlagCliente from "./flagCliente";
import MenuCliente from "./menuCliente";
import FelixAvatar from "../../../../util/ui/felixAvatar";
import StatusCalculoChip from "./statusCalculoChip";
import type { StatusCalculo } from "./statusCalculoChip";

type Props = {
  cliente: Usuario;
};

/** Data sempre em mono tabular, mesmo no meio de uma frase. */
function DataMono({ children }: { children: ReactNode }) {
  return (
    <Typography variant="dataMono" component="span" fontSize="inherit">
      {children}
    </Typography>
  );
}

export default function CardCliente({ cliente }: Props) {
  const { usuario, visualizado } = useUsuario();

  let statusCalculo: StatusCalculo;
  let textoCalculo;

  const atrasado = !!cliente.atualizadoAte?.isBefore(
    moment.tz("America/Sao_Paulo"),
    "day"
  );
  const semCalculo = !cliente?.estatisticas?.impostos;
  if (semCalculo) {
    statusCalculo = "error";
    textoCalculo = "Cálculos ainda não iniciados.";
  } else {
    if (!atrasado) {
      statusCalculo = "success";
      textoCalculo = "Cálculos atualizados.";
    } else {
      statusCalculo = "warning";
      textoCalculo = (
        <>
          {"Cálculos desatualizados desde "}
          <DataMono>{cliente.atualizadoAte.format("DD/MM/YYYY")}</DataMono>.
        </>
      );
    }
  }

  const selecionado = visualizado === cliente.id;

  return (
    <FelixCard
      key={cliente.id}
      sx={(theme) => ({
        // Seleção é tinta accent + hairline reforçada, nunca sombra.
        borderStyle: "solid",
        borderWidth: selecionado ? 2 : 1,
        borderColor: selecionado
          ? theme.palette.accentInk
          : theme.palette.divider,
        backgroundColor: selecionado
          ? alpha(theme.palette.primary.main, 0.06)
          : theme.palette.background.paper,
        transition: theme.transitions.create(
          ["border-color", "background-color"],
          { duration: 120 }
        ),
      })}
    >
      <CardHeader
        disableTypography
        avatar={
          <Radio
            size="small"
            checked={selecionado}
            onClick={() =>
              usuario &&
              updateDoc(new UsuarioDb().doc({ usuarioId: usuario?.id }), {
                asUser: cliente.id,
              })
            }
          />
        }
        title={<StatusCalculoChip status={statusCalculo} />}
        action={
          <Box display="flex" flexDirection="row">
            <FlagCliente cliente={cliente} />
            <MenuCliente cliente={cliente} />
          </Box>
        }
      />
      <CardContent>
        <Box display="flex" flexDirection="column" alignItems="center">
          <FelixAvatar
            nome={cliente.nome}
            size={64}
            perfil={Perfil.INVESTIDOR}
            fotoDePerfil={cliente.fotoDePerfil}
          />

          <Typography
            variant="h3"
            align="center"
            color="text.primary"
            pt={2}
            pb={0.5}
          >
            {cliente.nome}
          </Typography>
          <Typography
            variant="dataMono"
            component="p"
            color="text.secondary"
            gutterBottom
          >
            {cliente.cpf ?? "-"}
          </Typography>
          <List sx={{ width: "100%", py: 0 }}>
            <ListItem alignItems="flex-start" disableGutters>
              <ListItemIcon
                sx={{ minWidth: 40, justifyContent: "center", mt: 0.5 }}
              >
                <IconCalculator size={20} />
              </ListItemIcon>
              <ListItemText
                primaryTypographyProps={{
                  variant: "body2",
                  color: "text.secondary",
                }}
                primary={textoCalculo}
              />
            </ListItem>

            <ListItem alignItems="flex-start" disableGutters>
              <ListItemIcon
                sx={{ minWidth: 40, justifyContent: "center", mt: 0.5 }}
              >
                <IconArchive size={20} />
              </ListItemIcon>
              <ListItemText
                primaryTypographyProps={{
                  variant: "body2",
                  color: "text.secondary",
                }}
                primary={
                  !!cliente.ultimaNota ? (
                    <>
                      {"Última operação em "}
                      <DataMono>
                        {cliente.ultimaNota.format("DD/MM/YYYY")}
                      </DataMono>
                      .
                    </>
                  ) : (
                    `Nenhuma nota adicionada.`
                  )
                }
              />
            </ListItem>
          </List>
        </Box>
      </CardContent>
    </FelixCard>
  );
}
